<script setup lang="ts">
type OfficialSiteDialogMode =
  | 'notice' // 特别提示弹窗，用户点击网址后继续判断官网是否可打开
  | 'unavailable' // 无法访问特别提示弹窗，提示当前网址有访问风险

const props = withDefaults(
  defineProps<{
    /** 弹窗显示状态。 */
    modelValue: boolean
    /** 弹窗模式：特别提示、无法访问。 */
    mode?: OfficialSiteDialogMode
    /** 特别提示文案列表，接口未返回时由父组件传入默认文案。 */
    notices?: readonly { content: string }[]
    /** 弹窗内展示的官网 URL。 */
    url?: string
    /** 交易商数据。 */
    traderData?: any
    /** 交易商标识。 */
    exchangeSlug?: string
  }>(),
  {
    mode: 'notice',
    notices: () => [],
    url: '',
  },
)

const matomo = useMatomo()

watch(
  () => props.modelValue,
  (visible) => {
    if (!import.meta.client) {
      return
    }

    if (visible) {
      matomo.trackEvent('broker_special_reminder_pop', {
        name: props.traderData?.basicInfo?.shortName,
        code: props.exchangeSlug,
      })
    }
  },
  { immediate: true },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: []
}>()

/** 关闭当前弹窗。 */
const closeDialog = () => {
  emit('update:modelValue', false)
}

const { t } = useAppI18n()

const trackReminder = (type: string) => {
  matomo.trackEvent('broker_special_reminder_pop', {
    af_type: type,
    name: props.traderData?.basicInfo?.shortName,
    code: props.exchangeSlug,
  })
}

const clickClose = () => {
  trackReminder('关闭')
  closeDialog()
}

/** 复制当前展示的官网地址，并给出成功提示。 */
const copyUrl = () => {
  if (!props.url) return

  const input = document.createElement('input')
  document.body.appendChild(input)
  input.setAttribute('value', props.url)
  input.select()
  document.execCommand('Copy')
  ElMessage({
    message: t('82423'),
    type: 'success',
    customClass: 'official-site-copy-message',
  })
  document.body.removeChild(input)
}

/** 无法访问弹窗内点击复制按钮。 */
const clickCopyUrl = () => {
  trackReminder('复制网址')
  copyUrl()
}

/** 特别提示弹窗内点击官网地址，交给父组件继续判断是否可打开。 */
const clickNoticeUrl = () => {
  emit('confirm')
}

/** 底部按钮动作：特别提示和无法访问弹窗均只关闭。 */
const confirmDialog = async () => {
  trackReminder('我已了解')
  closeDialog()
}
</script>

<template>
  <ElDialog
    :model-value="modelValue"
    width="540px"
    align-center
    append-to-body
    :show-close="false"
    :close-on-click-modal="false"
    class="official-site-dialog"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="overflow-hidden rounded-[28px] bg-[#fff]">
      <div class="flex flex-col bg-[#fff] px-[32px] pt-[32px]">
        <div class="flex w-full items-start space-x-[8px] border-b-[0.5px] border-solid border-[#0515240f] pb-[16px]">
          <div class="flex min-w-0 flex-1 flex-col space-y-[8px]">
            <div
              class="w-full overflow-hidden text-[20px] leading-[28px] font-[600] break-words text-ellipsis whitespace-nowrap text-[#292a2e]"
            >
              {{ t('82617') }}
            </div>
            <div
              v-if="props.mode === 'unavailable'"
              class="w-full text-[14px] leading-[22px] font-[400] break-words text-[#7d818a]"
            >
              {{ t('82618') }}
            </div>
          </div>
          <div class="flex h-[32px] w-[32px] shrink-0 cursor-pointer items-center justify-center" @click="clickClose">
            <CommonImage
              src="https://resources1.interface003.com/web30/2026/06/08/2af6f5ee-7ed0-4d4b-8dff-dcf5540ccb14.png"
              width="32"
              height="32"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col items-center bg-[#fff] px-[32px] pt-[20px] pb-[32px]">
        <div v-if="mode === 'notice'" class="flex w-full flex-col space-y-[16px]">
          <div v-for="(notice, index) in notices" :key="index" class="flex w-full items-start space-x-[8px]">
            <div
              class="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-tl-[12px] rounded-br-[12px] bg-[#f8f8f8]"
            >
              <span class="text-[13px] leading-[20px] font-[500] whitespace-nowrap text-[#292a2e]">
                {{ index + 1 }}
              </span>
            </div>
            <div class="min-w-0 flex-1 text-[14px] leading-[22px] font-[400] break-words text-[#292a2e]">
              {{ notice.content }}
            </div>
          </div>
          <div
            v-if="url"
            class="flex w-full cursor-pointer items-center rounded-[8px] bg-[#f8f8f8] px-[16px] py-[12px]"
            @click="clickNoticeUrl"
          >
            <div
              class="whitespace-wrap line-clamp-2 min-w-0 flex-1 overflow-hidden text-[14px] leading-[22px] font-[400] text-ellipsis text-[#292a2e]"
            >
              <span dir="ltr"> {{ url }}</span>
            </div>
          </div>
        </div>

        <div v-else class="flex w-full flex-col space-y-[8px]">
          <div class="flex w-full items-center space-x-[12px] rounded-[8px] bg-[#f8f8f8] px-[16px] py-[12px]">
            <div
              class="whitespace-wrap line-clamp-2 min-w-0 flex-1 overflow-hidden text-[14px] leading-[22px] font-[400] text-ellipsis text-[#292a2e]"
            >
              <span dir="ltr"> {{ url }}</span>
            </div>
            <div
              class="flex h-[16px] w-[16px] shrink-0 cursor-pointer items-center justify-center"
              @click="clickCopyUrl"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/06/15/ad3b8026-f4fa-4142-89a4-53fbce8c7a8a.png"
                width="16"
                height="16"
              />
            </div>
          </div>
          <div class="w-full text-[14px] leading-[22px] font-[400] break-words text-[#e2483d]">
            {{ t('82619') }}
          </div>
        </div>

        <div
          class="hover-mask-effect mt-[20px] flex h-[46px] cursor-pointer items-center justify-center rounded-[100px] bg-[#112D73] px-[64px] py-[12px] text-[14px] leading-[22px] font-[600] whitespace-nowrap text-[#fff]"
          @click="confirmDialog"
        >
          {{ t('82616') }}
        </div>
      </div>
    </div>
  </ElDialog>
</template>

<style scoped lang="less">
:global(.official-site-dialog) {
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  padding: 0;
}

:global(.official-site-dialog .el-dialog__header) {
  display: none;
  padding: 0;
  margin: 0;
}

:global(.official-site-dialog .el-dialog__body) {
  padding: 0;
}

:global(.official-site-copy-message) {
  z-index: 3000 !important;
}
</style>
